import { Connector } from "appworks/server/connectors/connector";
import { Contract } from "appworks/utils/contracts/contract";
import { Sequence } from "appworks/utils/contracts/sequence";
import { logger } from "appworks/utils/logger";
import ReconnectingWebSocket from "reconnecting-websocket";
import { Signal } from "signals";

export class SocketConnector implements Connector {
    public static REQUEST_TIMEOUT = Number.MAX_SAFE_INTEGER;

    protected webSocket: WebSocket | ReconnectingWebSocket;
    protected errorSignal: Signal = new Signal();
    protected requestResolve: (value: any) => void;
    protected openResolve: (value: any) => void;
    protected requestTimeoutId: number;

    public sendRequest(request: SocketRequest): Contract<{}> {
        const actions = [];

        if (!this.webSocket) {
            actions.push(() => new Contract<void>((resolve) => {
                try {
                    this.webSocket = new ReconnectingWebSocket(request.url);
                } catch (e) {
                    logger.warn("Reconnecting web socket failed, falling back to native");
                    this.webSocket = new WebSocket(request.url);
                }
                (this.webSocket as any).addEventListener("error", this.onErrorListener);
                (this.webSocket as any).addEventListener("message", this.onMessage);
                (this.webSocket as any).addEventListener("open", this.onOpen);
                (this.webSocket as any).addEventListener("close", this.onClose);

                // Close socket connection on window close.
                window.onbeforeunload = () => {
                    this.close();
                };

                if (this.webSocket.readyState === WebSocket.OPEN) {
                    resolve(null);
                } else {
                    this.openResolve = resolve;
                }
            }));
        }

        actions.push(
            () => new Contract((resolve) => {
                this.requestTimeoutId = window.setTimeout(
                    () => this.onTimeout(),
                    SocketConnector.REQUEST_TIMEOUT
                );
                this.requestResolve = resolve;
                this.webSocket.send(JSON.stringify(request.body));
            })
        );

        return new Sequence(actions);
    }

    public onError(method: (message: string, fatal: boolean) => void) {
        this.errorSignal.add(method);
    }

    public close(): void {
        (this.webSocket as any).removeEventListener("error", this.onErrorListener);
        (this.webSocket as any).removeEventListener("message", this.onMessage);
        (this.webSocket as any).removeEventListener("open", this.onOpen);
        (this.webSocket as any).removeEventListener("close", this.onClose);

        this.webSocket.close();
    }

    protected onConnect() {
        if (this.openResolve) {
            this.openResolve(null);
            this.openResolve = null;
        } else {
            logger.log("SocketConnector: Websocket Reconnected.");
        }
    }

    protected onDisconnect() {
        logger.log("SocketConnector: Websocket Disconnected.");
        this.close();
        this.errorSignal.dispatch("err.dis", true);
    }

    protected onTimeout(): void {
        logger.log("SocketConnector: Request Timed Out.");
        this.close();
        this.errorSignal.dispatch("err.dis", true);
    }

    protected resolveRequest(message: any) {
        if (this.requestResolve) {
            window.clearTimeout(this.requestTimeoutId);
            this.requestResolve(message);
            this.requestResolve = null;
        }
    }

    private onErrorListener = (error: ErrorEvent) => {
        this.close();
        this.errorSignal.dispatch("err.dis", true);
    }

    private onMessage = (response: any) => {
        this.resolveRequest(response);
    }

    private onOpen = () => {
        this.onConnect();
    }

    private onClose = () => {
        this.onDisconnect();
    }
}

export class SocketRequest {
    constructor(public body: any, public url: string = "") {
    }
}

export const socketConnector = new SocketConnector();
